// The starlight floor: the light the catalog's stars give a horizontal surface, from the table in the stars message (broadcast_server._starlight_field). No three.js.
//
// The stars are fixed in universe space and the world spins about a fixed axis, so the local up vector moves round one circle and the floor is a periodic function of the angle on it. The table
// holds the floor (RGB, in the sun message's units, with the world's starlight_scale applied, extinction of the ground's air) for up = `ref_up` turned about `axis` by 2 pi k / samples. The
// reader here takes the up vector the observer has in universe space (row 2 of the frame the registry gives, bodies.ts starView), finds its angle on that circle, and interpolates.
// It is the floor at the surface of the home planet: it knows nothing of the eye's height or of another world's sky, which the caller leaves out (DefaultSimRenderer.observerSun).
import type { V3 } from './atmospherePhysics';

export interface StarlightField {
  samples: number;
  axis: V3;
  ref_up: V3;
  table: string;                    // base64 of `samples` x 3 little-endian float32
}

export class Starlight {
  private constructor(
    private readonly n: number,
    private readonly axis: V3,
    private readonly a: V3,          // ref_up with the axis taken out
    private readonly table: Float32Array,
  ) {}

  /** The reader for a message's field, or null if it is missing or malformed. */
  static from(f: StarlightField | null | undefined): Starlight | null {
    if (!f || !(f.samples >= 2) || f.axis?.length !== 3 || f.ref_up?.length !== 3 || typeof f.table !== 'string') return null;
    const bin = atob(f.table);
    if (bin.length !== f.samples * 12) return null;
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    const table = new Float32Array(bytes.buffer);
    const ax = norm(f.axis);
    return new Starlight(f.samples, ax, perp(f.ref_up, ax), table);
  }

  /**
   * The floor at the up vector `up` (universe space, any length) into `out`. A vector on the axis has no angle, and the floor then does not change with it: the first sample is returned.
   * @returns out
   */
  at(up: ArrayLike<number>, out: number[]): number[] {
    const b = perp([up[0]!, up[1]!, up[2]!], this.axis), a = this.a, x = this.axis;
    const cr: V3 = [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
    const s = x[0] * cr[0] + x[1] * cr[1] + x[2] * cr[2], c = a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
    let t = s * s + c * c > 1e-18 ? Math.atan2(s, c) / (2 * Math.PI) : 0;     // turns, -0.5..0.5
    t -= Math.floor(t);
    const p = t * this.n, i = Math.floor(p) % this.n, f = p - Math.floor(p), j = (i + 1) % this.n, T = this.table;
    for (let k = 0; k < 3; k++) out[k] = T[3 * i + k]! * (1 - f) + T[3 * j + k]! * f;
    return out;
  }
}

function norm(v: ArrayLike<number>): V3 {
  const l = Math.hypot(v[0]!, v[1]!, v[2]!) || 1;
  return [v[0]! / l, v[1]! / l, v[2]! / l];
}

/** `v` with its part along the unit `axis` taken out. */
function perp(v: ArrayLike<number>, axis: V3): V3 {
  const d = v[0]! * axis[0] + v[1]! * axis[1] + v[2]! * axis[2];
  return [v[0]! - d * axis[0], v[1]! - d * axis[1], v[2]! - d * axis[2]];
}
